import OCTContextBase from "./octContextBase";
import { octThicknessGradient, octColorSettings, generatePalette, zeissThicknessGradient, zeissDifferenceGradient, calculatePixelDifference } from "../canvas/canvasUtils";
import { defaultSize } from "../canvas/geometry";
import { calculateThicknessMapAdjustment, getThicknessMapSize } from "./octUtils";
import { SeriesTypes, COLOR_TYPE } from "../config/config";
import store from "../store";

export default class CirrusComparisonViewContext extends OCTContextBase {
  constructor(group, exam, view) {
    super(group, exam, "cirrusComparisonView");

    this.view = view;

    this.loadComposite = this.seriesType == SeriesTypes.OpticDisc ? true : false;
    this.loadHD = false;
    this.loadCScan = this.seriesType == SeriesTypes.OpticDisc ? true : false;

    this.octColorSettings = octColorSettings();
    this.thicknessMapColorSettings = null;

    this.slicePalette = octThicknessGradient;
    this.thicknessMapPalette = zeissThicknessGradient;

    if (store.getters.userPrefCirrusView == COLOR_TYPE.color)
      this.toggleColorPalette();

    this.showOverlay = true;
    this.overlayOpacity = 1;
    this.showETDRS = true;
    this.showSegmentation = false;

    this.relativeMapSize = defaultSize();

    this.thicknessMapMinThicknessUm = 0;
    this.thicknessMapMaxThicknessUm = 500;
    this.thicknessMapThicknessStepUm = 100;

    this.hasDifferenceMap = false;
    this.otherExSrNo = null;
    this.alignFoveas = null;
    this.differenceCanvas = null;
    this.differenceMapColorSettings = null;
    this.differenceMapMinThicknessUm = -150;
    this.differenceMapMaxThicknessUm = 150;
    this.differenceMapThicknessStepUm = 75;
    this.differenceMapPalette = zeissDifferenceGradient;
    this.differenceAnalysis = null;
  }

  startupTask() {
    return store.dispatch("getOCTInfo", { exSrNo: this.exSrNo, groupName: this.groupName })
      .then(data => this.setOCTInfo(data.data))
      .then(this.refreshImages);
  }

  refreshImages() {
    return store.dispatch("loadOCTImagesForMultiview");
  }

  setOCTInfo(data) {
    super.setOCTInfo(data);

    if (this.thicknessMap) {
      let colorPalette = generatePalette(this.thicknessMapPalette);
      let linearAdjustment = calculateThicknessMapAdjustment(this.thicknessMapMinThicknessUm,
        this.thicknessMapMaxThicknessUm, this.thicknessMap.depthSpacingMm);
      this.thicknessMapColorSettings = { colorPalette, linearAdjustment };

      // calculate the thickness map here in case the image was loaded previously
      if (this.thicknessMap.imgHtmlObject) this.loadThicknessMap(this.thicknessMap);
    }

    this.relativeMapSize = getThicknessMapSize(this);

    store.commit("calculateDifferenceAnalysis");
  }

  toggleSegmentation() {
    this.showSegmentation = !this.showSegmentation;
  }
  toggleETDRS() {
    this.showETDRS = !this.showETDRS;
  }
  toggleOverlay() {
    this.showOverlay = !this.showOverlay;
  }
  updateOverlayOpacity(opacity) {
    this.overlayOpacity = opacity;
  }

  toggleColorPalette() {
    this.octColorSettings.isInverted = false;
    if (this.octColorSettings.colorPalette) this.octColorSettings.colorPalette = null;
    else this.octColorSettings.colorPalette = this.getSlicePalette();
  }
  toggleInverted() {
    this.octColorSettings.colorPalette = null;
    this.octColorSettings.isInverted = !this.octColorSettings.isInverted;
  }
  getSlicePalette() {
    return generatePalette(this.slicePalette);
  }

  loadThicknessMap() {
    store.commit("calculateDifferenceMap");
  }

  calculateDifferenceMap(other, alignFoveas) {
    // don't recalculate difference map with same exam, just get it once
    if (this.hasDifferenceMap && other.exSrNo === this.otherExSrNo && alignFoveas === this.alignFoveas) return;

    if (!this.thicknessMap || !this.thicknessMap.imgHtmlObject) return;
    if (!other.thicknessMap || !other.thicknessMap.imgHtmlObject) return;
    // ensure that the difference gradient exists to calculate the difference image
    let baselineImg = other.thicknessMap.imgHtmlObject;
    let compareImg = this.thicknessMap.imgHtmlObject;

    // can't calculate difference if the images are different sizes
    if (baselineImg.width !== compareImg.width || baselineImg.height !== compareImg.height) return;

    this.generateDifferenceColorSettings();

    let canvas = document.createElement("canvas");
    let ctx = canvas.getContext("2d");
    let baselineData = this.getImgData(canvas, ctx, baselineImg);
    let baselineBuffer = new Uint32Array(baselineData.data.buffer);
    let compareData = this.getImgData(canvas, ctx, compareImg);
    let compareBuffer = new Uint32Array(compareData.data.buffer);
    let destData = ctx.createImageData(baselineImg.width, baselineImg.height);
    let writeArrayBuffer = new ArrayBuffer(destData.data.length);
    let writeBuffer = new Uint32Array(writeArrayBuffer);

    let { offsetX, offsetY } = this.calculateFoveaOffset(other, alignFoveas, compareImg, baselineImg);
    calculatePixelDifference(compareImg, offsetX, offsetY, compareBuffer, baselineBuffer, writeBuffer, this.differenceMapColorSettings);
    destData.data.set(new Uint8ClampedArray(writeArrayBuffer));
    ctx.putImageData(destData, 0, 0);
    this.differenceCanvas = canvas;
  }

  getImgData(canvas, ctx, img) {
    canvas.width = img.width;
    canvas.height = img.height;
    ctx.drawImage(img, 0, 0, img.width, img.height);
    return ctx.getImageData(0, 0, img.width, img.height);
  }

  generateDifferenceColorSettings() {
    if (this.differenceMapColorSettings) return;
    let colorPalette = generatePalette(this.differenceMapPalette);
    let linearAdjustment = calculateThicknessMapAdjustment(this.differenceMapMinThicknessUm,
      this.differenceMapMaxThicknessUm, this.thicknessMap.depthSpacingMm);
    this.differenceMapColorSettings = { colorPalette, linearAdjustment };
  }

  calculateFoveaOffset(other, alignFoveas, compareImg, baselineImg) {
    let offsetX = 0, offsetY = 0;
    if (alignFoveas && this.foveaOffset && other.foveaOffset) {
      let compareFoveaX = this.foveaOffset.x * compareImg.width;
      let compareFoveaY = this.foveaOffset.y * compareImg.height;
      let baselineFoveaX = other.foveaOffset.x * baselineImg.width;
      let baselineFoveaY = other.foveaOffset.y * baselineImg.height;
      offsetX = Math.floor(baselineFoveaX - compareFoveaX);
      offsetY = Math.floor(baselineFoveaY - compareFoveaY);
    }
    return { offsetX, offsetY };
  }

  calculateDifferenceAnalysis(other) {
    if (!this.thicknessAnalysis || !other.thicknessAnalysis) return;
    let compareTA = this.thicknessAnalysis;
    let otherTA = other.thicknessAnalysis;

    this.differenceAnalysis = {
      centerAverageThickness: compareTA.centerAverageThickness - otherTA.centerAverageThickness,
      centerRadius: {
        x: compareTA.centerRadius.x,
        y: compareTA.centerRadius.y
      },
      middleCircle: {
        radius: {
          x: compareTA.middleCircle.radius.x,
          y: compareTA.middleCircle.radius.y
        },
        eastValue: compareTA.middleCircle.eastValue - otherTA.middleCircle.eastValue,
        southValue: compareTA.middleCircle.southValue - otherTA.middleCircle.southValue,
        northValue: compareTA.middleCircle.northValue - otherTA.middleCircle.northValue,
        westValue: compareTA.middleCircle.westValue - otherTA.middleCircle.westValue
      },
      outerCircle: {
        radius: {
          x: compareTA.outerCircle.radius.x,
          y: compareTA.outerCircle.radius.y
        },
        eastValue: compareTA.outerCircle.eastValue - otherTA.outerCircle.eastValue,
        southValue: compareTA.outerCircle.southValue - otherTA.outerCircle.southValue,
        northValue: compareTA.outerCircle.northValue - otherTA.outerCircle.northValue,
        westValue: compareTA.outerCircle.westValue - otherTA.outerCircle.westValue
      }
    };
  }
}